import React, { useEffect, useMemo, useState } from 'react';
import T from 'i18n';
import MuiToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Controller, useForm } from 'react-hook-form';
import Form from 'react-bootstrap/Form';
import { caseAdminModalActions } from './enums';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Modal } from 'features/common/MaterialBasedComponents';
import TextForm from 'features/common/TextForm';
import { SelectForm, ToggleForm } from 'features/common';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  selectCaseSettingIManageImportEnabled,
  selectCurrentCaseTeamId,
  selectShowExhibitNumber,
  selectFolders,
} from 'common/selectors';
import { Chip } from '@mui/material';
import { Folder, CloseRounded } from '@mui/icons-material';
import FilesAndBundlesSelectionDialog from './SearchCenter/FilesAndBundlesSelectionDialog';
import { fetchTeams } from './redux/fetchTeams';

const PARTY_ROLE_OPTIONS = [
  { value: 'claimant', labelKey: 'generic.claimant' },
  { value: 'respondent', labelKey: 'generic.respondent' },
  { value: 'neutral', labelKey: 'generic.neutral' },
];

const CASE_INTELLIGENCE_VERSION_OPTIONS = [
  { value: 'v1', label: 'v1' },
  { value: 'v2', label: 'v2' },
  { value: 'v3', label: 'v3' },
];

const getSelectedPartyRole = (partyRole) => {
  if (!partyRole) return '';
  if (typeof partyRole === 'string') {
    return PARTY_ROLE_OPTIONS.some(({ value }) => value === partyRole) ? partyRole : '';
  }
  return PARTY_ROLE_OPTIONS.find(({ value }) => partyRole[value])?.value || '';
};

const PartyRoleToggle = ({ value, onChange }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        flexWrap: 'wrap',
      }}
    >
      <Form.Label style={{ marginBottom: 0 }}>{T.translate('case.partyRole')}</Form.Label>
      <ToggleButtonGroup
        exclusive
        value={value}
        onChange={(_event, nextValue) => {
          if (nextValue) {
            onChange(nextValue);
          }
        }}
        size="small"
        sx={{
          flexWrap: 'wrap',
          '& .MuiToggleButton-root': {
            borderColor: '#ced4da',
            color: '#495057',
            fontWeight: 500,
            fontSize: '11px',
            px: 2,
            py: 0.75,
            textTransform: 'none',
          },
          '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: '#7ba0f4',
            borderColor: '#7ba0f4',
            color: '#fff',
          },
          '& .MuiToggleButton-root.Mui-selected:hover': {
            backgroundColor: '#7ba0f4',
          },
        }}
      >
        {PARTY_ROLE_OPTIONS.map((option) => {
          return (
            <MuiToggleButton
              key={option.value}
              value={option.value}
              aria-label={T.translate(option.labelKey)}
            >
              {T.translate(option.labelKey)}
            </MuiToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </div>
  );
};

const AddNewGroup = ({ show, action, handleSave, handleClose }) => {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();
  const teamId = useSelector(selectCurrentCaseTeamId);
  const [teamOptions, setTeamOptions] = useState(null);

  useEffect(() => {
    if (!teamOptions) {
      dispatch(fetchTeams()).then((res) => {
        setTeamOptions(res.map((team) => ({ name: team.name, id: team.id })));
      });
    }
  }, [teamOptions, dispatch, teamId]);

  return (
    <Modal
      show={show}
      handleClose={handleClose}
      handleSave={handleSubmit((data) => {
        handleSave(action, data);
        handleClose();
      })}
      title={T.translate('case.addNewGroup')}
      saveTitle={T.translate('generic.add')}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Form.Group>
          <Row>
            <Col>
              <TextForm
                {...register('name', { required: true })}
                label={T.translate('case.groupName')}
                placeholder={T.translate('case.groupNamePlaceholder')}
                errors={errors}
                autofocus
              />
            </Col>
            <Col />
          </Row>
          <Row>
            <Col>
              <SelectForm
                menuPosition={'fixed'}
                name={'teams'}
                isMulti
                label={T.translate('case.selectTeam')}
                defaultValue={''}
                valueKey="id"
                labelKey="name"
                isClearable
                options={teamOptions}
                errors={errors}
                control={control}
                rules={{ required: true }}
                onBlur={() => {}}
              />
            </Col>
            <Col />
          </Row>
        </Form.Group>
      </Form>
    </Modal>
  );
};

const AddNewTeam = ({ show, action, handleSave, handleClose }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  return (
    <Modal
      show={show}
      handleClose={handleClose}
      handleSave={handleSubmit((data) => {
        handleSave(action, data);
        handleClose();
      })}
      title={T.translate('case.addNewTeam')}
      saveTitle={T.translate('generic.add')}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Form.Group>
          <Row>
            <Col>
              <TextForm
                {...register('name', { required: true })}
                label={T.translate('case.teamName')}
                placeholder={T.translate('case.teamNamePlaceholder')}
                errors={errors}
                autofocus
              />
            </Col>
            <Col />
          </Row>
        </Form.Group>
      </Form>
    </Modal>
  );
};

const RemoveTeam = ({ show, action, team, handleSave, handleClose }) => {
  return (
    <Modal
      show={show}
      handleClose={handleClose}
      handleSave={() => {
        handleSave(action, team.id);
        handleClose();
      }}
      title={T.translate('case.removeTeam')}
      saveTitle={T.translate('generic.remove')}
    >
      {T.translate('case.removeATeamMessage', {
        team: team.name,
      })}
    </Modal>
  );
};

const RemoveGroup = ({ show, action, team, handleSave, handleClose }) => {
  return (
    <Modal
      show={show}
      handleClose={handleClose}
      handleSave={() => {
        handleSave(action, team.id);
        handleClose();
      }}
      title={T.translate('case.removeGroup')}
      saveTitle={T.translate('generic.remove')}
    >
      {T.translate('case.removeAGroupMessage', {
        group: team.name,
      })}
    </Modal>
  );
};

const UpdateTeamName = ({ show, action, team, handleSave, handleClose }) => {
  const defaultValues = useMemo(
    () => ({
      name: team?.name || '',
      exhibitPrefix: team?.referencePrefix || '',
      exhibitNumberCounter: team?.exhibitNumberCounter || '',
      caseIntelligenceEnabled: team?.caseIntelligenceEnabled || false,
      caseIntelligenceVersion: team?.caseIntelligenceVersion || 'v1',
      forceCrawlOnNextReindex: team?.forceCrawlOnNextReindex || false,
      caseIntelFolderIds: team?.caseIntelFolderIds || [],
      partyRole: getSelectedPartyRole(team?.partyRole),
      iManageImportEnabled: team?.iManageImportEnabled || false,
    }),
    [team],
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues,
  });
  const showExhibitNumber = useSelector(selectShowExhibitNumber);
  const caseSettingIManageImportEnabled = useSelector(selectCaseSettingIManageImportEnabled);
  const isCaseIntelEnabled = watch('caseIntelligenceEnabled');
  const [bundlePickerAnchorEl, setBundlePickerAnchorEl] = useState(null);

  const allFolders = useSelector(selectFolders);
  const initialBundleObjects = useMemo(() => {
    const items = team?.caseIntelFolderIds || [];
    if (items.length === 0) return [];
    const allBundleTypes = [
      ...(allFolders.bundles || []),
      ...(allFolders.teamBundles || []),
      ...(allFolders.privateBundles || []),
      ...(allFolders.groupBundles || []),
      ...(allFolders.courtBundles || []),
      ...(allFolders.trialbooks || []),
    ];
    return items.map((item) => {
      if (typeof item === 'object' && item.id) {
        return { id: item.id, name: item.name || item.id };
      }
      const id = item;
      const found = allBundleTypes.find((b) => b.id === id);
      return { id, name: found?.name || id };
    });
  }, [team?.caseIntelFolderIds, allFolders]);

  const [selectedBundleObjects, setSelectedBundleObjects] = useState(initialBundleObjects);

  useEffect(() => {
    reset(defaultValues);
    setSelectedBundleObjects(initialBundleObjects);
  }, [defaultValues, reset, initialBundleObjects]);

  return (
    <Modal
      show={show}
      handleClose={handleClose}
      handleSave={handleSubmit((data) => {
        const selectedPartyRole = data.partyRole || getSelectedPartyRole(team?.partyRole);
        const updatedTeam = {
          ...team,
          name: data.name,
          referencePrefix: data.exhibitPrefix,
          exhibitNumberCounter: data.exhibitNumberCounter,
          caseIntelligenceEnabled: data.caseIntelligenceEnabled,
          caseIntelligenceVersion: data.caseIntelligenceEnabled
            ? (data.caseIntelligenceVersion ?? 'v1')
            : undefined,
          forceCrawlOnNextReindex: data.forceCrawlOnNextReindex,
          caseIntelFolderIds: data.caseIntelFolderIds || [],
          ...(selectedPartyRole && {
            partyRole: selectedPartyRole,
          }),
          iManageImportEnabled: data.iManageImportEnabled ?? team?.iManageImportEnabled ?? false,
        };
        handleSave(action, updatedTeam);
        handleClose();
      })}
      disableSave={!isDirty}
      title={T.translate('case.updateTeamName')}
      saveTitle={T.translate('generic.update')}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Form.Group style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <Row>
            <Col>
              <TextForm
                {...register('name', { required: true })}
                label={T.translate('case.teamName')}
                placeholder={T.translate('case.teamNamePlaceholder')}
                name="name"
                defaultValue={defaultValues.name}
                errors={errors}
                autofocus
              />
            </Col>
            {showExhibitNumber && (
              <Col>
                <Controller
                  name="exhibitPrefix"
                  control={control}
                  defaultValue={defaultValues.exhibitPrefix}
                  rules={{ required: true, pattern: /^[A-Z]{1,3}$/ }}
                  render={({ field }) => (
                    <TextForm
                      {...field}
                      label={T.translate('case.exhibitPrefix')}
                      placeholder={T.translate('case.teamPrefixPlaceholder')}
                      errors={errors}
                      onChange={(e) => {
                        const filteredValue = (e.target.value || '')
                          .replace(/[^A-Z]/g, '')
                          .slice(0, 3);
                        field.onChange(filteredValue);
                      }}
                      value={field.value || ''}
                      autofocus
                    />
                  )}
                />
              </Col>
            )}
            {showExhibitNumber && (
              <Col>
                <Controller
                  name="exhibitNumberCounter"
                  control={control}
                  defaultValue={defaultValues.exhibitNumberCounter}
                  rules={{ required: true, pattern: /^[1-9]\d*$/ }}
                  render={({ field }) => (
                    <TextForm
                      {...field}
                      label={T.translate('case.exhibitNumberCounter')}
                      placeholder={T.translate('case.exhibitNumberCounterPlaceholder')}
                      errors={errors}
                      onChange={(e) => {
                        const filteredValue = (e.target.value || '').replace(/\D/g, '');
                        field.onChange(filteredValue);
                      }}
                      value={field.value || ''}
                      autofocus
                    />
                  )}
                />
              </Col>
            )}
            <Col />
          </Row>
          <Row>
            <Col>
              <Controller
                name="partyRole"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <PartyRoleToggle value={value} onChange={onChange} />
                )}
              />
            </Col>
            <Col />
          </Row>
          <Row>
            <Col>
              <Controller
                name="caseIntelligenceEnabled"
                control={control}
                render={({ field: { value, onChange, ref } }) => (
                  <ToggleForm
                    ref={ref}
                    name="caseIntelligenceEnabled"
                    label={T.translate('case.enableCaseIntelligence')}
                    value={Boolean(value)}
                    onChange={(event) => onChange(event.target.checked)}
                  />
                )}
              />
            </Col>
            <Col />
            {caseSettingIManageImportEnabled && (
              <Col>
                <ToggleForm
                  {...register('iManageImportEnabled')}
                  label={T.translate('case.iManageEnabled')}
                  defaultValue={team.iManageImportEnabled || false}
                />
              </Col>
            )}
          </Row>
          {isCaseIntelEnabled && (
            <>
              <Row>
                <Col>
                  <SelectForm
                    menuPosition={'fixed'}
                    name="caseIntelligenceVersion"
                    label={T.translate('case.caseIntelligenceVersion')}
                    defaultValue={defaultValues.caseIntelligenceVersion}
                    valueKey="value"
                    labelKey="label"
                    options={CASE_INTELLIGENCE_VERSION_OPTIONS}
                    errors={errors}
                    control={control}
                  />
                </Col>
                <Col />
              </Row>
              <Row>
                <Col>
                  <Controller
                    name="forceCrawlOnNextReindex"
                    control={control}
                    render={({ field: { value, onChange, ref } }) => (
                      <ToggleForm
                        ref={ref}
                        name="forceCrawlOnNextReindex"
                        label={T.translate('case.forceCrawlOnReindex')}
                        value={Boolean(value)}
                        onChange={(event) => onChange(event.target.checked)}
                      />
                    )}
                  />
                </Col>
                <Col />
              </Row>
              <Row>
                <Col>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem',
                    }}
                  >
                    <Form.Label style={{ marginBottom: 0 }}>
                      {T.translate('case.crawlSummaryLocation')}
                    </Form.Label>
                    <Chip
                      size="small"
                      icon={
                        <span className="sticky-chip-icon">
                          <span className="sticky-chip-iconDefault" aria-hidden="true">
                            <Folder fontSize="small" />
                          </span>
                          {selectedBundleObjects.length > 0 && (
                            <span
                              className="sticky-chip-iconHover"
                              aria-hidden="true"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedBundleObjects([]);
                                setValue('caseIntelFolderIds', [], { shouldDirty: true });
                              }}
                            >
                              <CloseRounded fontSize="small" />
                            </span>
                          )}
                        </span>
                      }
                      label={
                        selectedBundleObjects.length > 0
                          ? `${((name) => (name.length > 20 ? name.substring(0, 20) + '...' : name))(selectedBundleObjects[0]?.name || '')}${
                              selectedBundleObjects.length > 1
                                ? ` & ${selectedBundleObjects.length - 1} more ...`
                                : ''
                            }`
                          : T.translate('generic.selectBundles') || 'Select Bundles'
                      }
                      clickable
                      sx={{
                        backgroundColor: 'transparent',
                        border: '1px solid',
                        borderColor: '#ced4da',
                        color: '#495057',
                        fontWeight: 500,
                        fontSize: '11px',
                        borderRadius: '4px',
                        padding: '5px 10px 5px 5px',
                        height: 'auto',
                        '& .MuiChip-label': {
                          padding: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                        '& .MuiChip-icon': {
                          color: '#495057',
                          marginLeft: '4px',
                          marginRight: '6px',
                        },
                        '& .sticky-chip-icon': {
                          position: 'relative',
                          width: '18px',
                          height: '18px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                        '& .sticky-chip-iconDefault': {
                          position: 'absolute',
                          inset: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        },
                        '& .sticky-chip-iconHover': {
                          position: 'absolute',
                          inset: 0,
                          display: 'none',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '18px',
                          height: '18px',
                          borderRadius: '999px',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid rgba(0, 0, 0, 0.18)',
                          color: '#111827',
                          cursor: 'pointer',
                        },
                        ...(selectedBundleObjects.length > 0
                          ? {
                              '&:hover .sticky-chip-iconDefault': {
                                display: 'none',
                              },
                              '&:hover .sticky-chip-iconHover': {
                                display: 'inline-flex',
                              },
                            }
                          : {}),
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          borderColor: '#ced4da',
                          color: '#495057',
                          '& .MuiChip-icon': {
                            color: '#495057',
                          },
                        },
                      }}
                      onClick={(event) => setBundlePickerAnchorEl(event.currentTarget)}
                    />
                  </div>
                </Col>
                <Col />
              </Row>
            </>
          )}
        </Form.Group>
      </Form>
      <FilesAndBundlesSelectionDialog
        variant="popover"
        anchorEl={bundlePickerAnchorEl}
        open={Boolean(bundlePickerAnchorEl)}
        onClose={() => setBundlePickerAnchorEl(null)}
        onSave={({ bundles }) => {
          setSelectedBundleObjects(bundles);
          setValue(
            'caseIntelFolderIds',
            bundles.map((b) => ({ id: b.id, name: b.name })),
            { shouldDirty: true },
          );
          setBundlePickerAnchorEl(null);
        }}
        initialSelectedFiles={[]}
        initialSelectedBundles={selectedBundleObjects}
        allowedMode="bundles"
      />
    </Modal>
  );
};

const CaseAdminModal = ({ show, action, team, handleClose, handleSave }) => {
  return (
    <>
      {action === caseAdminModalActions.addNewTeam && (
        <AddNewTeam show={show} action={action} handleClose={handleClose} handleSave={handleSave} />
      )}
      {action === caseAdminModalActions.updateTeamName && (
        <UpdateTeamName
          show={show}
          action={action}
          team={team}
          handleClose={handleClose}
          handleSave={handleSave}
        />
      )}
      {action === caseAdminModalActions.removeTeam && (
        <RemoveTeam
          show={show}
          action={action}
          team={team}
          handleClose={handleClose}
          handleSave={handleSave}
        />
      )}
      {action === caseAdminModalActions.addNewGroup && (
        <AddNewGroup
          show={show}
          action={action}
          handleClose={handleClose}
          handleSave={handleSave}
        />
      )}
      {action === caseAdminModalActions.removeGroup && (
        <RemoveGroup
          show={show}
          action={action}
          team={team}
          handleClose={handleClose}
          handleSave={handleSave}
        />
      )}
    </>
  );
};

export default CaseAdminModal;
